import { motion } from "framer-motion";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import StarBackground from "./components/StarBackground";
import M31 from "./pages/M31";
import AndromedaDetails from "./pages/AndromedaDetails";
import WhirlpoolDetails from "./pages/WhirlpoolDetails";
import OrionDetails from "./pages/OrionDetails";
import BodeDetails from "./pages/BodeDetails";
import HorseheadDetails from "./pages/HorseheadDetails";
import HeartDetails from "./pages/HeartDetails";
import SnowballDetails from "./pages/SnowballDetails";
import PleiadesDetails from "./pages/PleiadesDetails";
import "@/App.css";
import { Sparkles, ChevronDown } from "lucide-react";

// Translation object
const translations = {
  en: {
    explore: "Explore",
    discover: "Discover",
    heroTitle: "WELCOME TO THE",
    heroTitleHighlight: "INFINITE UNIVERSE",
    heroSubtitle: "Experience the vastness of space through stunning celestial imagery and interactive exploration. Journey through galaxies, nebulae, and the mysteries of the universe.",
    myWork: "My Work",
    learnMore: "Learn More",
    featuresTitle: "Discover the Universe",
    feature1Title: "Deep Space",
    feature1Desc: "Explore distant galaxies, nebulae, and cosmic phenomena billions of light-years away.",
    feature2Title: "Interactive",
    feature2Desc: "Navigate through space with smooth, immersive animations and real-time interactions.",
    feature3Title: "HD Imagery",
    feature3Desc: "Experience breathtaking high-definition images captured by space telescopes.",
    footer: "© 2025 Infinite Universe. Exploring the infinite universe."
  },
  no: {
    explore: "Utforsk",
    discover: "Oppdag",
    heroTitle: "VELKOMMEN TIL DET",
    heroTitleHighlight: "UENDELIGE UNIVERSET",
    heroSubtitle: "Opplev universets vidstrakte rom gjennom fantastiske himmelbilder og interaktiv utforskning. Reis gjennom galakser, tåker og universets mysterier.",
    myWork: "Mitt Arbeid",
    learnMore: "Lær Mer",
    featuresTitle: "Oppdag Universet",
    feature1Title: "Dypt Rom",
    feature1Desc: "Utforsk fjerne galakser, tåker og kosmiske fenomener milliarder av lysår unna.",
    feature2Title: "Interaktiv",
    feature2Desc: "Naviger gjennom rommet med jevne, oppslukende animasjoner og sanntidsinteraksjoner.",
    feature3Title: "HD-bilder",
    feature3Desc: "Opplev fantastiske høyoppløselige bilder tatt av romteleskoper.",
    footer: "© 2025 Uendelig Univers. Utforsker det uendelige universet."
  },
  pl: {
    explore: "Eksploruj",
    discover: "Odkryj",
    heroTitle: "WITAMY W",
    heroTitleHighlight: "NIESKOŃCZONYM WSZECHŚWIECIE",
    heroSubtitle: "Doświadcz ogromności kosmosu poprzez wspaniałe zdjęcia ciał niebieskich i interaktywną eksplorację. Podróżuj przez galaktyki, mgławice i tajemnice wszechświata.",
    myWork: "Moja Praca",
    learnMore: "Dowiedz Się Więcej",
    featuresTitle: "Odkryj Wszechświat",
    feature1Title: "Głęboki Kosmos",
    feature1Desc: "Odkrywaj odległe galaktyki, mgławice i zjawiska kosmiczne oddalone o miliardy lat świetlnych.",
    feature2Title: "Interaktywny",
    feature2Desc: "Poruszaj się po kosmosie z płynnymi, wciągającymi animacjami i interakcjami w czasie rzeczywistym.",
    feature3Title: "Obrazy HD",
    feature3Desc: "Doświadcz zapierających dech w piersiach obrazów w wysokiej rozdzielczości przechwyconych przez teleskopy kosmiczne.",
    footer: "© 2025 Nieskończony Wszechświat. Eksplorujemy nieskończony wszechświat."
  }
};

function HomePage() {
  const [language, setLanguage] = useState('en');
  const t = translations[language];

  return (
    <div className="App min-h-screen">
      <StarBackground />
      
      {/* Glassmorphism Navbar */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10"
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Sparkles className="text-purple-400 w-6 h-6" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              INFINITE UNIVERSE
            </span>
          </motion.div>
          
          <motion.div 
            className="flex gap-6 items-center"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <button 
              className="text-white/80 hover:text-white transition-colors duration-300"
              data-testid="nav-explore-btn"
            >
              {t.explore}
            </button>
            <button 
              className="text-white/80 hover:text-white transition-colors duration-300"
              data-testid="nav-discover-btn"
            >
              {t.discover}
            </button>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-8" data-testid="hero-section">
        {/* Radial glow effect */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(20, 30, 60, 0.15) 0%, rgba(0, 0, 0, 0) 70%)'
          }}
        />
        
        <div className="relative z-10 text-center max-w-6xl">
          {/* Main Title */}
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-7xl font-black mb-6"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            data-testid="hero-title"
          >
            {t.heroTitle}
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400">
              {t.heroTitleHighlight}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            className="text-base md:text-lg text-white/70 mb-12 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            data-testid="hero-subtitle"
          >
            {t.heroSubtitle}
          </motion.p>

          {/* Floating Galaxy Image */}
          <motion.div
            className="relative mx-auto mb-12 flex justify-center items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              y: [0, -20, 0]
            }}
            transition={{ 
              opacity: { duration: 1, delay: 0.9 },
              scale: { duration: 1, delay: 0.9 },
              y: { 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut",
                delay: 1.9
              }
            }}
            data-testid="floating-galaxy-container"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_5111349b-9fb5-449f-9203-3144233eff50/artifacts/yj9g8wol_2025-12-14_02-19-59.png"
              alt="Gravitational Lensing Galaxy Cluster"
              className="rounded-lg shadow-[0_0_100px_rgba(112,66,248,0.4)] border border-white/20 w-full max-w-xl mx-auto block"
              data-testid="galaxy-image"
            />
            
            {/* Glow ring around image */}
            <div className="absolute inset-0 rounded-lg blur-3xl bg-blue-900/10 -z-10" />
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <Link to="/m31">
              <button 
                className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold tracking-wide uppercase text-sm backdrop-blur-md transition-all duration-300"
                data-testid="cta-explore-btn"
              >
                {t.myWork}
              </button>
            </Link>
            <button 
              className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold tracking-wide uppercase text-sm backdrop-blur-md transition-all duration-300"
              data-testid="cta-learn-btn"
            >
              {t.learnMore}
            </button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: 1,
            y: [0, 10, 0]
          }}
          transition={{ 
            opacity: { duration: 1, delay: 1.5 },
            y: { 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut"
            }
          }}
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-8 h-8 text-white/50" />
        </motion.div>
      </section>

      {/* Featured Section */}
      <section className="relative py-24 px-8" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-16"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            data-testid="features-title"
          >
            {t.featuresTitle}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <motion.div 
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:border-purple-500/50 transition-all duration-500"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              data-testid="feature-card-1"
            >
              <div className="w-16 h-16 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.feature1Title}
              </h3>
              <p className="text-white/70 text-base">
                {t.feature1Desc}
              </p>
            </motion.div>

            {/* Feature Card 2 */}
            <motion.div 
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:border-cyan-500/50 transition-all duration-500"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              data-testid="feature-card-2"
            >
              <div className="w-16 h-16 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.feature2Title}
              </h3>
              <p className="text-white/70 text-base">
                {t.feature2Desc}
              </p>
            </motion.div>

            {/* Feature Card 3 */}
            <motion.div 
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:border-pink-500/50 transition-all duration-500"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              data-testid="feature-card-3"
            >
              <div className="w-16 h-16 bg-pink-500/20 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-pink-400" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.feature3Title}
              </h3>
              <p className="text-white/70 text-base">
                {t.feature3Desc}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-8 border-t border-white/10" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/50 text-sm">
            {t.footer}
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/m31" element={<M31 />} />
        <Route path="/andromeda-details" element={<AndromedaDetails />} />
        <Route path="/whirlpool-details" element={<WhirlpoolDetails />} />
        <Route path="/orion-details" element={<OrionDetails />} />
        <Route path="/bode-details" element={<BodeDetails />} />
        <Route path="/horsehead-details" element={<HorseheadDetails />} />
        <Route path="/heart-details" element={<HeartDetails />} />
        <Route path="/snowball-details" element={<SnowballDetails />} />
        <Route path="/pleiades-details" element={<PleiadesDetails />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
